import * as React from "react";
import { useState, useRef, useCallback, useEffect } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AvatarEditorResult {
  dataURI: string;
  blob: Blob;
}

export interface AvatarEditorProps {
  /** When set, displays a read-only circular preview of this URL (no editing). */
  imageUrl?: string;
  /** Size of the output square image in pixels (default 256) */
  outputSize?: number;
  /** Diameter of the circular preview mask in pixels (default 280) */
  previewSize?: number;
  /** Called after every edit with the current cropped image. */
  onComplete: (result: AvatarEditorResult) => void;
  /** Optional label shown above the file input */
  label?: string;
  /** Accept string forwarded to the file input (default "image/*") */
  accept?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

/** Load an HTMLImageElement from a File. */
const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = function (err) {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

/** Extract the first client position from a mouse or touch event. */
const clientPos = (
  e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent,
): { x: number; y: number } => {
  if ("touches" in e) {
    var t = (e as TouchEvent).touches[0] || (e as TouchEvent).changedTouches[0];
    return { x: t.clientX, y: t.clientY };
  }
  return { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY };
};

// ─── Shared button font ─────────────────────────────────────────────────────

var BUTTON_FONT: React.CSSProperties = {
  fontFamily: "'Rubik', sans-serif",
  fontSize: 16,
  fontWeight: 400,
  borderRadius: 8,
  padding: "8px 18px",
  margin: 3,
  cursor: "pointer",
  userSelect: "none",
  display: "inline-flex",
  alignItems: "center",
  border: "none",
};

// ─── Component ───────────────────────────────────────────────────────────────

const AvatarEditor: React.FC<AvatarEditorProps> = ({
  imageUrl,
  outputSize = 256,
  previewSize = 280,
  onComplete,
  label = "Choose an image",
  accept = "image/*",
}) => {
  // ── State ────────────────────────────────────────────────────────────────

  const [showUrlPreview, setShowUrlPreview] = useState(!!imageUrl);
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  // Transform state
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offset, setOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Track whether the dropzone file input is focused (for visible outline)
  const [dropzoneFocused, setDropzoneFocused] = useState(false);

  // Drag refs
  const isDragging = useRef(false);
  const dragStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const offsetAtDragStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const offsetRef = useRef(offset);
  offsetRef.current = offset;

  // Canvas ref
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  // File input ref — used for programmatic focus
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ref for the latest scale (used by the native wheel listener)
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  // Flag: when set, focus the file input after the next render shows the
  // dropzone. Cleared once focus is applied.
  const shouldFocusDropzone = useRef(false);

  // If the parent changes imageUrl, reset to the read-only view.
  useEffect(function () {
    if (imageUrl) {
      setShowUrlPreview(true);
      setImage(null);
      setScale(1);
      setRotation(0);
      setOffset({ x: 0, y: 0 });
    }
  }, [imageUrl]);

  // When the dropzone becomes visible and focus was requested, move focus
  // to the file input so keyboard users land directly on it.
  useEffect(function () {
    if (shouldFocusDropzone.current && !image && !showUrlPreview) {
      if (fileInputRef.current) {
        fileInputRef.current.focus();
      }
      shouldFocusDropzone.current = false;
    }
  }, [image, showUrlPreview]);

  // ── File handling ────────────────────────────────────────────────────────

  var handleFileChange = useCallback(
    function (e: React.ChangeEvent<HTMLInputElement>) {
      var files = e.target.files;
      if (!files || files.length === 0) return;
      loadImage(files[0]).then(function (img) {
        setShowUrlPreview(false);
        setImage(img);
        setScale(1);
        setRotation(0);
        setOffset({ x: 0, y: 0 });
      });
    },
    [],
  );

  // ── Drawing ──────────────────────────────────────────────────────────────

  var drawPreview = useCallback(
    function () {
      var canvas = previewCanvasRef.current;
      if (!canvas || !image) return;

      var ctx = canvas.getContext("2d");
      if (!ctx) return;

      var size = previewSize;
      canvas.width = size;
      canvas.height = size;
      ctx.clearRect(0, 0, size, size);

      ctx.save();
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.clip();

      ctx.fillStyle = "#e2e8f0";
      ctx.fillRect(0, 0, size, size);

      ctx.translate(size / 2 + offset.x, size / 2 + offset.y);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(scale, scale);

      var aspect = image.width / image.height;
      var drawW: number;
      var drawH: number;
      if (aspect >= 1) {
        drawH = size;
        drawW = size * aspect;
      } else {
        drawW = size;
        drawH = size / aspect;
      }
      ctx.drawImage(image, -drawW / 2, -drawH / 2, drawW, drawH);
      ctx.restore();

      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, size, size);
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2, true);
      ctx.fillStyle = "rgba(0,0,0,0.45)";
      ctx.fill();
      ctx.restore();
    },
    [image, scale, rotation, offset, previewSize],
  );

  useEffect(function () {
    drawPreview();
  }, [drawPreview]);

  // ── Auto-export on every transform change ────────────────────────────────

  var exportTimeoutRef = useRef<number | undefined>(undefined);

  var emitResult = useCallback(
    function () {
      if (!image) return;

      var canvas = document.createElement("canvas");
      canvas.width = outputSize;
      canvas.height = outputSize;
      var ctx = canvas.getContext("2d");
      if (!ctx) return;

      var ratio = outputSize / previewSize;
      ctx.translate(
        outputSize / 2 + offset.x * ratio,
        outputSize / 2 + offset.y * ratio,
      );
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(scale * ratio, scale * ratio);

      var aspect = image.width / image.height;
      var drawW: number;
      var drawH: number;
      if (aspect >= 1) {
        drawH = previewSize;
        drawW = previewSize * aspect;
      } else {
        drawW = previewSize;
        drawH = previewSize / aspect;
      }
      ctx.drawImage(image, -drawW / 2, -drawH / 2, drawW, drawH);

      var dataURI = canvas.toDataURL("image/png");
      canvas.toBlob(function (blob) {
        if (blob) {
          onComplete({ dataURI: dataURI, blob: blob });
        }
      }, "image/png");
    },
    [image, outputSize, previewSize, scale, rotation, offset, onComplete],
  );

  useEffect(
    function () {
      if (!image) return;

      if (exportTimeoutRef.current !== undefined) {
        clearTimeout(exportTimeoutRef.current);
      }

      exportTimeoutRef.current = window.setTimeout(function () {
        emitResult();
      }, 150);

      return function () {
        if (exportTimeoutRef.current !== undefined) {
          clearTimeout(exportTimeoutRef.current);
        }
      };
    },
    [emitResult, image],
  );

  // ── Mouse / touch drag (pan) ─────────────────────────────────────────────

  var handleDragMove = useCallback(function (e: MouseEvent | TouchEvent) {
    if (!isDragging.current) return;
    var pos = clientPos(e);
    setOffset({
      x: offsetAtDragStart.current.x + (pos.x - dragStart.current.x),
      y: offsetAtDragStart.current.y + (pos.y - dragStart.current.y),
    });
  }, []);

  var handleDragEnd = useCallback(function () {
    isDragging.current = false;
  }, []);

  useEffect(function () {
    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDragMove, { passive: false });
    document.addEventListener("touchend", handleDragEnd);

    return function () {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", handleDragMove);
      document.removeEventListener("touchend", handleDragEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  var startDrag = useCallback(
    function (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
      e.preventDefault();
      isDragging.current = true;
      var pos = clientPos(e as any);
      dragStart.current = pos;
      offsetAtDragStart.current = { ...offsetRef.current };
    },
    [],
  );

  // ── Wheel zoom (native listener for React 16 passive-event fix) ──────────

  useEffect(
    function () {
      var canvas = previewCanvasRef.current;
      if (!canvas) return;

      var onWheel = function (e: WheelEvent) {
        e.preventDefault();
        setScale(clamp(scaleRef.current - e.deltaY * 0.001, 0.1, 5));
      };

      canvas.addEventListener("wheel", onWheel, { passive: false });
      return function () {
        if(null!=canvas) {
          canvas.removeEventListener("wheel", onWheel);
        }
      };
    },
    [image],
  );

  // ── Button handlers ──────────────────────────────────────────────────────

  var rotateBy = function (deg: number) {
    setRotation(function (r) { return (r + deg) % 360; });
  };

  var zoomBy = function (delta: number) {
    setScale(function (s) { return clamp(s + delta, 0.1, 5); });
  };

  var panBy = function (dx: number, dy: number) {
    setOffset(function (o) { return { x: o.x + dx, y: o.y + dy }; });
  };

  // Cancel discards edits and returns to the previous state:
  // if an imageUrl was provided, show that read-only preview;
  // otherwise, go back to the file-picker dropzone and focus it.
  var handleCancel = function () {
    setImage(null);
    setScale(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
    if (imageUrl) {
      setShowUrlPreview(true);
    } else {
      shouldFocusDropzone.current = true;
    }
  };

  // "Change Image" from the read-only URL preview goes to the dropzone
  // and focuses the file input.
  var handleChangeImage = function () {
    setShowUrlPreview(false);
    setImage(null);
    setScale(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
    shouldFocusDropzone.current = true;
  };

  // ── Styles ───────────────────────────────────────────────────────────────

  var containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    fontFamily: "'Rubik', sans-serif",
  };

  var btnSecondary: React.CSSProperties = {
    ...BUTTON_FONT,
    background: "rgb(239, 239, 239)",
    color: "#003595",
  };

  // WCAG AA requires >= 4.5:1 contrast for normal text.
  // #B91C1C on rgb(239,239,239) gives ~8:1 — comfortably passes AA.
  var btnCancel: React.CSSProperties = {
    ...BUTTON_FONT,
    background: "rgb(239, 239, 239)",
    color: "#B91C1C",
  };

  var toolbarStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
  };

  var sectionLabel: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "#64748b",
    marginBottom: 2,
  };

  var groupStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: "0 8px",
  };

  // Dropzone base style
  var dropzoneStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 40,
    border: "2px dashed #cbd5e1",
    borderRadius: 12,
    cursor: "pointer",
    color: "#475569",
    width: previewSize,
    textAlign: "center",
    position: "relative",
  };

  // When the file input inside is focused, show a prominent outline
  if (dropzoneFocused) {
    dropzoneStyle = {
      ...dropzoneStyle,
      outline: "2px solid #003595",
      outlineOffset: 2,
      borderColor: "#003595",
    };
  }

  // The file input is visually hidden but remains in the tab order.
  // position: absolute keeps it inside the label so clicking the label
  // still delegates to the input; opacity: 0 hides it visually; and
  // width/height: 100% means the entire dropzone area is the hit target.
  var srOnlyInputStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    opacity: 0,
    cursor: "pointer",
  };

  // ── Render: read-only imageUrl preview ───────────────────────────────────

  if (showUrlPreview && imageUrl) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            width: previewSize,
            height: previewSize,
            borderRadius: "50%",
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
          }}
        >
          <img
            src={imageUrl}
            alt="Current avatar"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            style={btnSecondary}
            aria-label="Change image and choose a new avatar"
            onClick={handleChangeImage}
          >
            Change Image
          </button>
        </div>
      </div>
    );
  }

  // ── Render: file picker dropzone ─────────────────────────────────────────

  if (!image) {
    return (
      <div style={containerStyle}>
        <label style={dropzoneStyle}>
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginBottom: 12 }}
            aria-hidden="true"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          <span style={{ fontSize: 14 }}>{label}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            onFocus={function () { setDropzoneFocused(true); }}
            onBlur={function () { setDropzoneFocused(false); }}
            style={srOnlyInputStyle}
            aria-label="Upload an image file for your avatar"
          />
        </label>
      </div>
    );
  }

  // ── Render: editor ───────────────────────────────────────────────────────

  return (
    <div style={containerStyle} role="region" aria-label="Avatar image editor">
      {/* Preview canvas */}
      <div
        style={{
          position: "relative",
          width: previewSize,
          height: previewSize,
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        }}
      >
        <canvas
          ref={previewCanvasRef}
          width={previewSize}
          height={previewSize}
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          role="img"
          aria-label="Avatar preview. Drag to reposition the image, scroll to zoom."
          style={{
            cursor: "grab",
            touchAction: "none",
            display: "block",
          }}
        />
      </div>

      {/* Hint */}
      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 12, marginBottom: 12 }}>
        Drag to pan &middot; Scroll to zoom
      </div>

      {/* Toolbar */}
      <div
        style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}
        role="toolbar"
        aria-label="Image adjustment controls"
      >
        {/* Rotate */}
        <div style={groupStyle} role="group" aria-label="Rotation controls">
          <span style={sectionLabel} aria-hidden="true">Rotate</span>
          <div style={toolbarStyle}>
            <button
              type="button"
              style={btnSecondary}
              onClick={function () { rotateBy(-90); }}
              aria-label="Rotate image 90 degrees counter-clockwise"
            >
              &#8634; 90°
            </button>
            <button
              type="button"
              style={btnSecondary}
              onClick={function () { rotateBy(90); }}
              aria-label="Rotate image 90 degrees clockwise"
            >
              &#8635; 90°
            </button>
          </div>
        </div>

        {/* Zoom */}
        <div style={groupStyle} role="group" aria-label="Zoom controls">
          <span style={sectionLabel} aria-hidden="true">Zoom</span>
          <div style={toolbarStyle}>
            <button
              type="button"
              style={btnSecondary}
              onClick={function () { zoomBy(-0.1); }}
              aria-label="Zoom out by 10 percent"
            >
              &minus; Zoom
            </button>
            <span
              style={{
                fontSize: 13,
                color: "#64748b",
                alignSelf: "center",
                minWidth: 48,
                textAlign: "center",
                margin: "0 2px",
              }}
              aria-live="polite"
              aria-label={"Current zoom level: " + Math.round(scale * 100) + " percent"}
            >
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              style={btnSecondary}
              onClick={function () { zoomBy(0.1); }}
              aria-label="Zoom in by 10 percent"
            >
              + Zoom
            </button>
          </div>
        </div>

        {/* Pan */}
        <div style={groupStyle} role="group" aria-label="Pan controls">
          <span style={sectionLabel} aria-hidden="true">Pan</span>
          <div style={toolbarStyle}>
            <button
              type="button"
              style={btnSecondary}
              onClick={function () { panBy(-20, 0); }}
              aria-label="Pan image left"
            >
              &larr;
            </button>
            <button
              type="button"
              style={btnSecondary}
              onClick={function () { panBy(0, -20); }}
              aria-label="Pan image up"
            >
              &uarr;
            </button>
            <button
              type="button"
              style={btnSecondary}
              onClick={function () { panBy(0, 20); }}
              aria-label="Pan image down"
            >
              &darr;
            </button>
            <button
              type="button"
              style={btnSecondary}
              onClick={function () { panBy(20, 0); }}
              aria-label="Pan image right"
            >
              &rarr;
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ ...toolbarStyle, marginTop: 12 }}>
        <button
          type="button"
          style={btnCancel}
          onClick={handleCancel}
          aria-label="Cancel editing and return to previous view"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AvatarEditor;
